import { useEffect, useState } from "react";
import { ApiClientError, fetchReviewReport, postReviewReportGenerate } from "../../api/client";
import type { ReviewPeriodMode, ReviewReportResponse, ReviewStoreFilter } from "../../types/reviews";
import { StateBanner } from "../../components/StateBanner";
import { StoreCards } from "./components/StoreCards";

type Loadable<T> = { status: "loading" } | { status: "error"; message: string } | { status: "ok"; data: T };

interface ReportTabProps {
  period: ReviewPeriodMode;
  month: string;
  store: ReviewStoreFilter;
}

const STORE_LABEL: Record<ReviewStoreFilter, string> = { all: "全店舗", shopify: "Shopify", rakuten: "楽天市場", amazon: "Amazon" };

export function ReportTab({ period, month, store }: ReportTabProps) {
  const [report, setReport] = useState<Loadable<ReviewReportResponse>>({ status: "loading" });
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const load = () => {
    setReport({ status: "loading" });
    fetchReviewReport({ period, month, store })
      .then((data) => setReport({ status: "ok", data }))
      .catch((e: unknown) => setReport({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  };

  useEffect(load, [period, month, store]);

  const generate = async () => {
    setGenerating(true);
    setGenError(null);
    try {
      const res = await postReviewReportGenerate(month);
      setReport((cur) => (cur.status === "ok" ? { status: "ok", data: { ...cur.data, ai: res.ai } } : cur));
    } catch (e) {
      setGenError(e instanceof ApiClientError ? e.message : "AI要約の生成に失敗しました。");
    } finally {
      setGenerating(false);
    }
  };

  if (report.status === "loading") return <StateBanner kind="loading" message="読み込み中" />;
  if (report.status === "error") return <StateBanner kind="error" message={report.message} onRetry={load} />;

  const { data } = report;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rv-report-actions rv-no-print">
        <button className="rv-btn" disabled={generating} onClick={generate}>
          {generating ? "生成中…" : data.ai ? "AI要約を再生成" : "AI要約を生成"}
        </button>
        <button className="rv-btn rv-btn-primary" onClick={() => window.print()}>
          PDFで出力
        </button>
      </div>
      {genError && (
        <span className="rv-note rv-no-print" style={{ color: "var(--rv-bad)" }}>
          {genError}
        </span>
      )}

      <div className="rv-report-sheet">
        <div className="rv-report-head">
          <div>
            <div className="rv-report-title">STOQ レビュー月次レポート</div>
            <span className="rv-card-sub">{data.period.label}</span>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="rv-card-sub">対象: {STORE_LABEL[store]}</div>
            <div className="rv-card-sub">作成日 {new Date(data.updatedAt).toLocaleDateString("ja-JP")}</div>
          </div>
        </div>

        <div className="rv-kpi-grid">
          <div className="rv-kpi-card">
            <span className="rv-kpi-label">平均評価</span>
            <span className="rv-kpi-value">{data.kpis.avgRating !== null ? data.kpis.avgRating.toFixed(1) : "―"}</span>
          </div>
          <div className="rv-kpi-card">
            <span className="rv-kpi-label">レビュー件数</span>
            <span className="rv-kpi-value">{data.kpis.count}</span>
          </div>
          <div className="rv-kpi-card">
            <span className="rv-kpi-label">商品レビュー記入率</span>
            <span className="rv-kpi-value">{data.kpis.fillRate !== null ? `${data.kpis.fillRate}%` : "―"}</span>
          </div>
          <div className="rv-kpi-card">
            <span className="rv-kpi-label">低評価（★1〜2）</span>
            <span className="rv-kpi-value">{data.kpis.lowCount}</span>
          </div>
        </div>

        <div>
          <span className="rv-card-title">AI要約 </span>
          <span className="rv-tag rv-tag--info" style={{ marginLeft: 6 }}>
            自動生成
          </span>
          <p className="rv-ai-summary">{data.ai ? data.ai.summary : "まだAI要約が生成されていません。上の「AI要約を生成」から作成してください。"}</p>
        </div>

        {data.ai && (
          <div>
            <span className="rv-card-title">レビュー内容の話題</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              {data.ai.topics.map((t) => {
                const total = t.pos + t.neg || 1;
                return (
                  <div className="rv-report-topic-row" key={t.label}>
                    <span>{t.label}</span>
                    <div className="rv-report-topic-bar">
                      <span style={{ width: `${(t.pos / total) * 100}%`, background: "var(--rv-good)" }} />
                      <span style={{ width: `${(t.neg / total) * 100}%`, background: "var(--rv-bad)" }} />
                    </div>
                    <span className="rv-card-sub">
                      肯定{t.pos}・否定{t.neg}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="rv-two-col">
          <div>
            <span className="rv-card-title">店舗別サマリー</span>
            <div style={{ marginTop: 8 }}>
              <StoreCards stores={data.stores} />
            </div>
          </div>
          <div>
            <span className="rv-card-title">低評価対応サマリー</span>
            <p className="rv-card-sub">
              期間内の低評価（★1〜2）は{data.kpis.lowCount}件（前期{data.lowPrev}件）。うち未対応{data.low.open}件、対応中{data.low.wip}
              件、完了{data.low.done}件です。自動一次返信率は{data.low.autoReplyRate !== null ? `${data.low.autoReplyRate}%` : "―"}
              、平均完了時間は{data.low.avgCompleteHours !== null ? `${data.low.avgCompleteHours}時間` : "―"}でした。
            </p>
          </div>
        </div>

        <div className="rv-two-col">
          <div>
            <span className="rv-card-title">高評価TOP3</span>
            <ol style={{ margin: "8px 0 0", paddingLeft: 20, fontSize: 13 }}>
              {data.topProducts.map((p) => (
                <li key={p.name}>
                  {p.name}（{p.avg.toFixed(1)} ・ {p.count}件）
                </li>
              ))}
            </ol>
          </div>
          <div>
            <span className="rv-card-title">要注意商品</span>
            <ol style={{ margin: "8px 0 0", paddingLeft: 20, fontSize: 13 }}>
              {data.worstProducts.map((p) => (
                <li key={p.name}>
                  {p.name}（{p.avg.toFixed(1)} ・ {p.count}件）
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="rv-report-footnote">
          商品レビュー記入率 ＝ 商品レビューが書かれた注文数 ÷ 期間内注文件数（1注文で複数商品を書かれても1。楽天のショップレビューは含めず別表示）。話題分類はレビュー本文からのAI自動抽出であり、厳密な集計値ではありません。
        </div>
      </div>
    </div>
  );
}
